import { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import Modal from './Modal';
import { SkeletonTable } from './Skeleton';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
import './Boxes.css';

// Función helper para obtener el color del stock según el mínimo
const getStockColor = (qty: number, minQty: number) => {
  if (qty < minQty) {
    return 'var(--error)';
  } else if (qty <= minQty * 1.2) {
    return 'var(--warning)';
  } else {
    return 'var(--success)';
  }
};

// Componente para mostrar el stock mínimo con edición al hover
const MinQtyDisplay = ({
  minQty,
  boxTypeId,
  onEdit,
  canEdit,
}: {
  minQty: number;
  boxTypeId: number;
  onEdit: () => void;
  canEdit: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  if (!canEdit) {
    return <span>{minQty}</span>;
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        position: 'relative',
        padding: '0.25rem 2rem 0.25rem 0.5rem',
        borderRadius: '4px',
        cursor: 'pointer',
        backgroundColor: isHovered ? 'var(--bg-secondary)' : 'transparent',
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onEdit}
      title="Clic para editar mínimo"
    >
      <strong>{minQty}</strong>
      <span
        style={{
          position: 'absolute',
          right: '0.35rem',
          top: '50%',
          transform: 'translateY(-50%)',
          opacity: isHovered ? 1 : 0,
          pointerEvents: isHovered ? 'auto' : 'none',
          transition: 'opacity 0.2s',
        }}
      >
        <button
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '0.1rem',
            lineHeight: 1,
            fontSize: '0.9rem',
          }}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          title="Editar mínimo"
        >
          ✏️
        </button>
      </span>
    </span>
  );
};

// Componente para mostrar el stock actual con edición al hover
const QtyDisplay = ({
  qty,
  boxTypeId,
  minQty,
  onEdit,
  canEdit,
}: {
  qty: number;
  boxTypeId: number;
  minQty: number;
  onEdit: () => void;
  canEdit: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  if (!canEdit) {
    return (
      <span style={{ color: getStockColor(qty, minQty), fontWeight: 'bold' }}>
        {qty}
      </span>
    );
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        position: 'relative',
        padding: '0.25rem 2rem 0.25rem 0.5rem',
        borderRadius: '4px',
        cursor: 'pointer',
        backgroundColor: isHovered ? 'var(--bg-secondary)' : 'transparent',
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onEdit}
      title="Clic para editar stock"
    >
      <strong style={{ color: getStockColor(qty, minQty) }}>{qty}</strong>
      <span
        style={{
          position: 'absolute',
          right: '0.35rem',
          top: '50%',
          transform: 'translateY(-50%)',
          opacity: isHovered ? 1 : 0,
          pointerEvents: isHovered ? 'auto' : 'none',
          transition: 'opacity 0.2s',
        }}
      >
        <button
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '0.1rem',
            lineHeight: 1,
            fontSize: '0.9rem',
          }}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          title="Editar stock"
        >
          ✏️
        </button>
      </span>
    </span>
  );
};

interface BoxType {
  id: number;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

interface BoxStock {
  id: number;
  box_type_id: number;
  qty: number;
  min_qty: number;
  box_type: BoxType;
}

interface BoxOrder {
  id: number;
  code: string;
  proveedor: string | null;
  status: 'PENDING' | 'RECEIVED' | 'CANCELLED';
  created_at: string;
  expected_at: string | null;
  received_at: string | null;
  lines: Array<{
    id: number;
    box_type_id: number;
    qty_ordered: number;
    qty_received: number;
    box_type: BoxType;
  }>;
}

interface BoxEntry {
  id: number;
  box_type_id: number;
  qty: number;
  source: 'MANUAL' | 'ORDER' | 'ADJUSTMENT';
  box_order_id: number | null;
  notes: string | null;
  created_at: string;
  box_type: BoxType;
}

export default function Boxes() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const [activeTab, setActiveTab] = useState<'stock' | 'orders' | 'entries'>('stock');
  const [stock, setStock] = useState<BoxStock[]>([]);
  const [orders, setOrders] = useState<BoxOrder[]>([]);
  const [entries, setEntries] = useState<BoxEntry[]>([]);
  const [boxTypes, setBoxTypes] = useState<BoxType[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [isCreateEntryModalOpen, setIsCreateEntryModalOpen] = useState(false);
  const [isOrderDetailModalOpen, setIsOrderDetailModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<BoxOrder | null>(null);
  
  // Form data
  const [orderFormData, setOrderFormData] = useState({ proveedor: '', expected_at: '' });
  const [orderSelectedLines, setOrderSelectedLines] = useState<Map<number, number>>(new Map());
  const [entryFormData, setEntryFormData] = useState({ box_type_id: '', qty: '', source: 'MANUAL' as 'MANUAL' | 'ORDER' | 'ADJUSTMENT', notes: '' });
  const [orderLineQtyInputs, setOrderLineQtyInputs] = useState<Map<number, string>>(new Map());
  const [editingMinQty, setEditingMinQty] = useState<number | null>(null);
  const [editingMinQtyValue, setEditingMinQtyValue] = useState<string>('');
  const [editingQty, setEditingQty] = useState<number | null>(null);
  const [editingQtyValue, setEditingQtyValue] = useState<string>('');
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'stock') {
        await loadStock();
      } else if (activeTab === 'orders') {
        await loadOrders();
      } else if (activeTab === 'entries') {
        await loadEntries();
      }
      await loadBoxTypes();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadStock = async () => {
    const response = await api.get('/boxes/stock');
    setStock(response.data);
  };

  const loadOrders = async () => {
    const response = await api.get('/boxes/orders');
    setOrders(response.data);
  };

  const loadEntries = async () => {
    const response = await api.get('/boxes/entries');
    setEntries(response.data);
  };

  const loadBoxTypes = async () => {
    const response = await api.get('/boxes/types');
    setBoxTypes(response.data.filter((bt: BoxType) => bt.is_active));
  };

  const handleCreateOrder = async () => {
    if (orderSelectedLines.size === 0) {
      showError('Debes seleccionar al menos un tipo de estuche');
      return;
    }

    try {
      const lines = Array.from(orderSelectedLines.entries()).map(([box_type_id, qty_ordered]) => ({
        box_type_id,
        qty_ordered,
      }));

      await api.post('/boxes/orders', {
        proveedor: orderFormData.proveedor || null,
        expected_at: orderFormData.expected_at || null,
        lines,
      });

      showSuccess('Pedido creado correctamente');
      setIsCreateOrderModalOpen(false);
      setOrderFormData({ proveedor: '', expected_at: '' });
      setOrderSelectedLines(new Map());
      loadOrders();
      loadStock();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al crear pedido');
    }
  };

  const handleCreateEntry = async () => {
    if (!entryFormData.box_type_id || !entryFormData.qty) {
      showError('Debes completar todos los campos obligatorios');
      return;
    }

    try {
      await api.post('/boxes/entries', {
        box_type_id: parseInt(entryFormData.box_type_id, 10),
        qty: parseInt(entryFormData.qty, 10),
        source: entryFormData.source,
        notes: entryFormData.notes || null,
      });

      showSuccess('Entrada registrada correctamente');
      setIsCreateEntryModalOpen(false);
      setEntryFormData({ box_type_id: '', qty: '', source: 'MANUAL', notes: '' });
      loadEntries();
      loadStock();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al registrar entrada');
    }
  };

  const handleReceiveOrderLine = async (orderId: number, lineId: number) => {
    const qtyStr = orderLineQtyInputs.get(lineId);
    if (!qtyStr || parseInt(qtyStr, 10) <= 0) {
      showError('Debes ingresar una cantidad válida');
      return;
    }

    try {
      await api.post(`/boxes/orders/${orderId}/lines/${lineId}/receive`, {
        qty: parseInt(qtyStr, 10),
      });

      showSuccess('Línea recibida correctamente');
      setOrderLineQtyInputs(new Map());
      loadOrders();
      loadStock();
      if (selectedOrder) {
        const response = await api.get(`/boxes/orders/${selectedOrder.id}`);
        setSelectedOrder(response.data);
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al recibir línea');
    }
  };

  const handleOpenOrderDetail = async (order: BoxOrder) => {
    try {
      const response = await api.get(`/boxes/orders/${order.id}`);
      setSelectedOrder(response.data);
      const newQtyInputs = new Map<number, string>();
      response.data.lines.forEach((line: any) => {
        const pending = line.qty_ordered - line.qty_received;
        if (pending > 0) {
          newQtyInputs.set(line.id, pending.toString());
        }
      });
      setOrderLineQtyInputs(newQtyInputs);
      setIsOrderDetailModalOpen(true);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar detalles');
    }
  };

  const handleDeleteOrder = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este pedido?')) {
      return;
    }

    try {
      await api.delete(`/boxes/orders/${id}`);
      showSuccess('Pedido eliminado correctamente');
      loadOrders();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar pedido');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'var(--warning)';
      case 'RECEIVED':
        return 'var(--success)';
      case 'CANCELLED':
        return 'var(--error)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'RECEIVED':
        return 'Recibido';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const handleEditMinQty = (boxTypeId: number, currentMinQty: number) => {
    setEditingMinQty(boxTypeId);
    setEditingMinQtyValue(currentMinQty.toString());
  };

  const handleSaveMinQty = async (boxTypeId: number) => {
    try {
      const minQty = parseInt(editingMinQtyValue, 10);
      if (isNaN(minQty) || minQty < 0) {
        showError('El stock mínimo debe ser un número válido mayor o igual a 0');
        return;
      }

      await api.put(`/boxes/stock/${boxTypeId}`, { min_qty: minQty });
      showSuccess('Stock mínimo actualizado correctamente');
      setEditingMinQty(null);
      setEditingMinQtyValue('');
      loadStock();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al actualizar stock mínimo');
    }
  };

  const handleCancelEditMinQty = () => {
    setEditingMinQty(null);
    setEditingMinQtyValue('');
  };

  const handleEditQty = (boxTypeId: number, currentQty: number) => {
    setEditingQty(boxTypeId);
    setEditingQtyValue(currentQty.toString());
  };

  const handleSaveQty = async (boxTypeId: number) => {
    try {
      const qty = parseInt(editingQtyValue, 10);
      if (isNaN(qty) || qty < 0) {
        showError('El stock debe ser un número válido mayor o igual a 0');
        return;
      }

      await api.put(`/boxes/stock/${boxTypeId}`, { qty: qty });
      showSuccess('Stock actualizado correctamente');
      setEditingQty(null);
      setEditingQtyValue('');
      loadStock();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al actualizar stock');
    }
  };

  const handleCancelEditQty = () => {
    setEditingQty(null);
    setEditingQtyValue('');
  };

  const handleRecalculateMinStocks = async () => {
    try {
      setRecalculating(true);
      const response = await api.post('/boxes/recalculate-min-stocks');
      showSuccess(`Mínimos recalculados: ${response.data.updated} actualizados, ${response.data.created} creados`);
      loadStock();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al recalcular stocks mínimos');
    } finally {
      setRecalculating(false);
    }
  };

  if (loading && stock.length === 0 && orders.length === 0 && entries.length === 0) {
    return (
      <div className="tab-content">
        <SkeletonTable rows={5} columns={6} />
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="boxes-header">
        <h2>Gestión de Estuches & Packaging</h2>
        <div className="boxes-tabs">
          <button
            type="button"
            className={`boxes-tab ${activeTab === 'stock' ? 'active' : ''}`}
            onClick={() => setActiveTab('stock')}
          >
            Stock
          </button>
          <button
            type="button"
            className={`boxes-tab ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            Pedidos
          </button>
          <button
            type="button"
            className={`boxes-tab ${activeTab === 'entries' ? 'active' : ''}`}
            onClick={() => setActiveTab('entries')}
          >
            Entradas
          </button>
        </div>
      </div>

      {/* Stock Tab */}
      {activeTab === 'stock' && (
        <div className="boxes-section">
          <div className="section-header">
            <h3>Stock de Estuches</h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {canEdit('boxes') && (
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleRecalculateMinStocks}
                  disabled={recalculating}
                >
                  {recalculating ? 'Recalculando...' : '🔄 Recalcular Mínimos'}
                </button>
              )}
              {canEdit('boxes') && (
                <button type="button" className="btn btn-primary" onClick={() => setIsCreateEntryModalOpen(true)}>
                  Registrar Entrada
                </button>
              )}
            </div>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tipo de Estuche</th>
                  <th>Stock Actual</th>
                  <th>Stock Mínimo</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {stock
                  .filter((item) => item.box_type.code !== 'COLGANTES') // Filtrar colgantes legacy
                  .map((item) => {
                    // Determinar qué tipos de artículos usan esta caja (igual que en Dashboard)
                    let productTypes = '';
                    if (item.box_type.code === 'ANILLOS') {
                      productTypes = 'Anillos';
                    } else if (item.box_type.code === 'PULSERAS_COLLARES') {
                      productTypes = 'Pulseras, Pendants, Collares y Colgantes';
                    } else {
                      productTypes = item.box_type.name;
                    }

                    return (
                      <tr key={item.id}>
                        <td>
                          <strong>{item.box_type.name}</strong>
                          {productTypes && (
                            <div className="text-secondary" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                              <strong>Para:</strong> {productTypes}
                            </div>
                          )}
                          {item.box_type.description && (
                            <div className="text-secondary">{item.box_type.description}</div>
                          )}
                        </td>
                        <td>
                          {editingQty === item.box_type_id ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <input
                                type="number"
                                min="0"
                                className="input"
                                value={editingQtyValue}
                                onChange={(e) => setEditingQtyValue(e.target.value)}
                                style={{ width: '80px', textAlign: 'center' }}
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveQty(item.box_type_id);
                                  } else if (e.key === 'Escape') {
                                    handleCancelEditQty();
                                  }
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => handleSaveQty(item.box_type_id)}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              >
                                ✓
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={handleCancelEditQty}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <QtyDisplay
                              qty={item.qty}
                              boxTypeId={item.box_type_id}
                              minQty={item.min_qty}
                              onEdit={() => handleEditQty(item.box_type_id, item.qty)}
                              canEdit={canEdit('boxes')}
                            />
                          )}
                        </td>
                        <td>
                          {editingMinQty === item.box_type_id ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <input
                                type="number"
                                min="0"
                                className="input"
                                value={editingMinQtyValue}
                                onChange={(e) => setEditingMinQtyValue(e.target.value)}
                                style={{ width: '80px', textAlign: 'center' }}
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveMinQty(item.box_type_id);
                                  } else if (e.key === 'Escape') {
                                    handleCancelEditMinQty();
                                  }
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => handleSaveMinQty(item.box_type_id)}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              >
                                ✓
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={handleCancelEditMinQty}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <MinQtyDisplay
                              minQty={item.min_qty}
                              boxTypeId={item.box_type_id}
                              onEdit={() => handleEditMinQty(item.box_type_id, item.min_qty)}
                              canEdit={canEdit('boxes')}
                            />
                          )}
                        </td>
                        <td>
                          {item.qty < item.min_qty ? (
                            <span className="badge badge-error">Stock Bajo</span>
                          ) : item.qty <= item.min_qty * 1.2 ? (
                            <span className="badge badge-warning">Atención</span>
                          ) : (
                            <span className="badge badge-success">Normal</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="boxes-section">
          <div className="section-header">
            <h3>Pedidos de Estuches</h3>
            {canEdit('boxes') && (
              <button type="button" className="btn btn-primary" onClick={() => setIsCreateOrderModalOpen(true)}>
                Nuevo Pedido
              </button>
            )}
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Proveedor</th>
                  <th>Estado</th>
                  <th>Fecha Creación</th>
                  <th>Fecha Esperada</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <strong>{order.code}</strong>
                    </td>
                    <td>{order.proveedor || '-'}</td>
                    <td>
                      <span
                        className="badge"
                        style={{ backgroundColor: getStatusColor(order.status) }}
                      >
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td>{formatDate(order.created_at)}</td>
                    <td>{order.expected_at ? formatDate(order.expected_at) : '-'}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleOpenOrderDetail(order)}
                        >
                          Ver Detalles
                        </button>
                        {canEdit('boxes') && order.status === 'PENDING' && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteOrder(order.id)}
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Entries Tab */}
      {activeTab === 'entries' && (
        <div className="boxes-section">
          <div className="section-header">
            <h3>Entradas de Estuches</h3>
            {canEdit('boxes') && (
              <button type="button" className="btn btn-primary" onClick={() => setIsCreateEntryModalOpen(true)}>
                Registrar Entrada
              </button>
            )}
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo de Estuche</th>
                  <th>Cantidad</th>
                  <th>Origen</th>
                  <th>Notas</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatDateTime(entry.created_at)}</td>
                    <td>
                      <strong>{entry.box_type.name}</strong>
                    </td>
                    <td>{entry.qty}</td>
                    <td>
                      {entry.source === 'MANUAL' && 'Manual'}
                      {entry.source === 'ORDER' && 'Pedido'}
                      {entry.source === 'ADJUSTMENT' && 'Ajuste'}
                    </td>
                    <td>{entry.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Order Modal */}
      {isCreateOrderModalOpen && (
        <Modal
          isOpen={true}
          title="Nuevo Pedido de Estuches"
          onClose={() => setIsCreateOrderModalOpen(false)}
          size="large"
        >
          <div className="modal-content">
            <div className="form-group">
              <label>Proveedor</label>
              <input
                type="text"
                value={orderFormData.proveedor}
                onChange={(e) => setOrderFormData({ ...orderFormData, proveedor: e.target.value })}
                placeholder="Nombre del proveedor"
              />
            </div>
            <div className="form-group">
              <label>Fecha Esperada</label>
              <input
                type="date"
                value={orderFormData.expected_at}
                onChange={(e) => setOrderFormData({ ...orderFormData, expected_at: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Tipos de Estuches</label>
              <div className="checklist">
                {boxTypes.map((boxType) => {
                  const qty = orderSelectedLines.get(boxType.id) || 0;
                  return (
                    <div key={boxType.id} className="checklist-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={orderSelectedLines.has(boxType.id)}
                          onChange={(e) => {
                            const newMap = new Map(orderSelectedLines);
                            if (e.target.checked) {
                              newMap.set(boxType.id, 1);
                            } else {
                              newMap.delete(boxType.id);
                            }
                            setOrderSelectedLines(newMap);
                          }}
                        />
                        {boxType.name}
                      </label>
                      {orderSelectedLines.has(boxType.id) && (
                        <input
                          type="number"
                          min="1"
                          value={qty}
                          onChange={(e) => {
                            const newMap = new Map(orderSelectedLines);
                            newMap.set(boxType.id, parseInt(e.target.value, 10) || 1);
                            setOrderSelectedLines(newMap);
                          }}
                          className="qty-input"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setIsCreateOrderModalOpen(false)}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleCreateOrder}>
                Crear Pedido
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Create Entry Modal */}
      {isCreateEntryModalOpen && (
        <Modal
          isOpen={true}
          title="Registrar Entrada de Estuches"
          onClose={() => setIsCreateEntryModalOpen(false)}
        >
          <div className="modal-content">
            <div className="form-group">
              <label>Tipo de Estuche *</label>
              <select
                value={entryFormData.box_type_id}
                onChange={(e) => setEntryFormData({ ...entryFormData, box_type_id: e.target.value })}
              >
                <option value="">Seleccionar...</option>
                {boxTypes.map((boxType) => (
                  <option key={boxType.id} value={boxType.id}>
                    {boxType.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Cantidad *</label>
              <input
                type="number"
                min="1"
                value={entryFormData.qty}
                onChange={(e) => setEntryFormData({ ...entryFormData, qty: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Origen</label>
              <select
                value={entryFormData.source}
                onChange={(e) => setEntryFormData({ ...entryFormData, source: e.target.value as any })}
              >
                <option value="MANUAL">Manual</option>
                <option value="ORDER">Pedido</option>
                <option value="ADJUSTMENT">Ajuste</option>
              </select>
            </div>
            <div className="form-group">
              <label>Notas</label>
              <textarea
                value={entryFormData.notes}
                onChange={(e) => setEntryFormData({ ...entryFormData, notes: e.target.value })}
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setIsCreateEntryModalOpen(false)}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleCreateEntry}>
                Registrar Entrada
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Order Detail Modal */}
      {isOrderDetailModalOpen && selectedOrder && (
        <Modal
          isOpen={true}
          title={`Pedido ${selectedOrder.code}`}
          onClose={() => setIsOrderDetailModalOpen(false)}
          size="large"
        >
          <div className="modal-content">
            <div className="order-info">
              <div><strong>Proveedor:</strong> {selectedOrder.proveedor || '-'}</div>
              <div><strong>Estado:</strong> {getStatusLabel(selectedOrder.status)}</div>
              <div><strong>Fecha Creación:</strong> {formatDate(selectedOrder.created_at)}</div>
              {selectedOrder.expected_at && (
                <div><strong>Fecha Esperada:</strong> {formatDate(selectedOrder.expected_at)}</div>
              )}
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tipo de Estuche</th>
                    <th>Cantidad Pedida</th>
                    <th>Cantidad Recibida</th>
                    <th>Pendiente</th>
                    {canEdit('boxes') && selectedOrder.status === 'PENDING' && <th>Recibir</th>}
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.lines.map((line) => {
                    const pending = line.qty_ordered - line.qty_received;
                    return (
                      <tr key={line.id}>
                        <td>{line.box_type.name}</td>
                        <td>{line.qty_ordered}</td>
                        <td>{line.qty_received}</td>
                        <td>{pending}</td>
                        {canEdit('boxes') && selectedOrder.status === 'PENDING' && pending > 0 && (
                          <td>
                            <div className="receive-line">
                              <input
                                type="number"
                                min="1"
                                max={pending}
                                value={orderLineQtyInputs.get(line.id) || ''}
                                onChange={(e) => {
                                  const newMap = new Map(orderLineQtyInputs);
                                  newMap.set(line.id, e.target.value);
                                  setOrderLineQtyInputs(newMap);
                                }}
                                className="qty-input"
                                placeholder={`Máx: ${pending}`}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => handleReceiveOrderLine(selectedOrder.id, line.id)}
                              >
                                Recibir
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setIsOrderDetailModalOpen(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

